import { useLanguage } from "@/contexts/LanguageContext";
import { Navbar } from "@/components/Navbar";
import { OrganizersSection2026 } from "@/components/sections/OrganizersSection2026";

const Index2026 = () => {
  const { t } = useLanguage();

  return (
    <div className="min-h-screen">
      <Navbar />
      
      {/* Hero Section */}
      <section className="hero-gradient min-h-[60vh] flex items-center justify-center pt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-sm text-gray-600 mb-4 italic">{t('event.patronage')}</p>
          <h2 className="text-xl text-primary mb-2">{t('event.callForPapers')}</h2>
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-primary mb-4">
            {t('event.title')}
          </h1>
          <p className="text-2xl sm:text-3xl text-gray-600 mb-4">
            {t('event.subtitle')}
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-8">
            <div className="text-lg font-semibold">{t('event.date')}</div>
            <div className="hidden sm:block">•</div>
            <div className="text-lg font-semibold">{t('event.location')}</div>
          </div>
        </div>
      </section>

      <OrganizersSection2026 />

      {/* Context Section */}
      <section className="section-padding bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-8">{t('objectives.title')}</h2>
          <div className="prose prose-lg mx-auto">
            <p className="text-gray-600 mb-4">{t('about.context')}</p>
            <p className="text-gray-600 mb-6">{t('about.mauritania')}</p>
            
            <h3 className="text-xl font-semibold mb-4">{t('objectives.title')}</h3>
            <ul className="max-w-3xl mx-auto space-y-4">
              {(t('objectives.list') as string[]).map((objective, index) => (
                <li key={index} className="flex items-start">
                  <span className="text-primary mr-2">•</span>
                  <span>{objective}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Thematic Axes Section */}
      <section className="section-padding bg-white">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-8">{t('topics.title')}</h2>
          
          {/* Axis 1 */}
          <div className="mb-8">
            <h3 className="text-xl font-semibold text-primary mb-4">{t('axis1.title')}</h3>
            <ul className="max-w-3xl mx-auto space-y-2">
              {(t('axis1.topics') as string[]).map((topic, index) => (
                <li key={index} className="flex items-start">
                  <span className="text-primary mr-2">•</span>
                  <span>{topic}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Axis 2 */}
          <div className="mb-8">
            <h3 className="text-xl font-semibold text-primary mb-4">{t('axis2.title')}</h3>
            <ul className="max-w-3xl mx-auto space-y-2">
              {(t('axis2.topics') as string[]).map((topic, index) => (
                <li key={index} className="flex items-start">
                  <span className="text-primary mr-2">•</span>
                  <span>{topic}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Axis 3 */}
          <div className="mb-8">
            <h3 className="text-xl font-semibold text-primary mb-4">{t('axis3.title')}</h3>
            <ul className="max-w-3xl mx-auto space-y-2">
              {(t('axis3.topics') as string[]).map((topic, index) => (
                <li key={index} className="flex items-start">
                  <span className="text-primary mr-2">•</span>
                  <span>{topic}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Submission Guidelines Section */}
      <section className="section-padding bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-8">{t('submission.title')}</h2>
          <div className="max-w-3xl mx-auto space-y-6">
            <p>{t('submission.criteria')}</p>
            <ul className="list-disc pl-6 space-y-2">
              {(t('submission.requirements') as string[]).map((req, index) => (
                <li key={index}>{req}</li>
              ))}
            </ul>
            <p>{t('submission.format')}</p>
            <p className="mt-4">{t('submission.abstract')}</p>
            <ul className="list-disc pl-6 space-y-2">
              {(t('submission.abstract.requirements') as string[]).map((info, index) => (
                <li key={index}>{info}</li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Deadlines Section */}
      <section className="section-padding bg-white">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-8">{t('deadlines.title')}</h2>
          <div className="max-w-3xl mx-auto space-y-4">
            <div className="flex justify-between items-center border-b pb-2">
              <span>{t('deadlines.submission')}</span>
              <span className="font-semibold">{t('deadlines.submission.date')}</span>
            </div>
            <div className="flex justify-between items-center border-b pb-2">
              <span>{t('deadlines.notification')}</span>
              <span className="font-semibold">{t('deadlines.notification.date')}</span>
            </div>
            <div className="flex justify-between items-center border-b pb-2">
              <span>{t('deadlines.final')}</span>
              <span className="font-semibold">{t('deadlines.final.date')}</span>
            </div>
            <div className="flex justify-between items-center border-b pb-2">
              <span>{t('deadlines.decision')}</span>
              <span className="font-semibold">{t('deadlines.decision.date')}</span>
            </div>
            <div className="flex justify-between items-center">
              <span>{t('deadlines.conference')}</span>
              <span className="font-semibold">{t('deadlines.conference.date')}</span>
            </div>
          </div>
          <p className="text-center mt-6 text-primary font-medium">{t('submission.email')}</p>
        </div>
      </section>

      {/* Scientific Committee Section */}
      <section className="section-padding bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-8">{t('committee.title')}</h2>
          <div className="max-w-3xl mx-auto">
            <p className="text-center text-gray-600">{t('committee.members')}</p>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="section-padding bg-white">
        <div className="max-w-7xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-center mb-4">{t('contact.title')}</h2>
          <p className="mb-2">{t('contact.info')}</p>
          <p className="text-primary font-medium">{t('contact.emails')}</p>
        </div>
      </section>
    </div>
  );
};

export default Index2026;
